/**
 * WebSocket Configuration for AWS API Gateway
 *
 * Main FSDS platform uses NEXT_PUBLIC_WEBSOCKET_URL.
 * Edunodes tenant communities use NEXT_PUBLIC_TENANT_WEBSOCKET_URL.
 */

import { isFsdsHostname } from '@/utils/domain-branding-resolver'

export interface WebSocketConfig {
  /** AWS API Gateway WebSocket URL */
  apiGatewayUrl: string
  /** AWS Region */
  region: string
  /** Maximum number of reconnection attempts */
  maxReconnectAttempts: number
  /** Initial reconnection interval in milliseconds */
  reconnectInterval: number
  /** Heartbeat interval in milliseconds */
  heartbeatInterval: number
  /** Connection timeout in milliseconds */
  connectionTimeout: number
  /** Enable debug logging */
  debug: boolean
}

const DEFAULT_MAIN_WEBSOCKET_URL =
  'wss://axkuaasioj.execute-api.ap-southeast-1.amazonaws.com/prod'

const DEFAULT_TENANT_WEBSOCKET_URL =
  'wss://lkkizt0up4.execute-api.ap-southeast-1.amazonaws.com/prod'

function normalizeWebSocketBaseUrl(url: string): string {
  return url.trim().replace(/\/$/, '')
}

/** Pick main FSDS vs tenant community WebSocket gateway from the browser host. */
export function resolveWebSocketBaseUrl(hostname?: string | null): string {
  const isTenantHost =
    typeof hostname === 'string' && hostname.trim().length > 0 && !isFsdsHostname(hostname)

  if (isTenantHost) {
    const tenantUrl = process.env.NEXT_PUBLIC_TENANT_WEBSOCKET_URL?.trim()
    return normalizeWebSocketBaseUrl(tenantUrl || DEFAULT_TENANT_WEBSOCKET_URL)
  }

  const mainUrl = process.env.NEXT_PUBLIC_WEBSOCKET_URL?.trim()
  return normalizeWebSocketBaseUrl(mainUrl || DEFAULT_MAIN_WEBSOCKET_URL)
}

function buildWebSocketConfig(hostname?: string | null): WebSocketConfig {
  const base = resolveWebSocketBaseUrl(hostname)
  return {
    apiGatewayUrl: `${base}/`,
    region: process.env.NEXT_PUBLIC_AWS_REGION || 'ap-southeast-1',
    maxReconnectAttempts: 5,
    reconnectInterval: 1000,
    heartbeatInterval: 30000,
    connectionTimeout: 10000,
    debug: process.env.NODE_ENV === 'development',
  }
}

/** SSR / platform default — main FSDS gateway. */
export const defaultWebSocketConfig: WebSocketConfig = buildWebSocketConfig()

/**
 * Validates the WebSocket configuration
 */
export function validateWebSocketConfig(config: WebSocketConfig): string[] {
  const errors: string[] = []

  if (!config.apiGatewayUrl) {
    errors.push('WebSocket API Gateway URL is required')
  }

  if (!config.apiGatewayUrl.startsWith('wss://') && !config.apiGatewayUrl.startsWith('ws://')) {
    errors.push('WebSocket API Gateway URL must start with wss:// or ws://')
  }

  if (!config.region) {
    errors.push('AWS Region is required')
  }

  if (config.maxReconnectAttempts < 0) {
    errors.push('Max reconnect attempts must be non-negative')
  }

  if (config.reconnectInterval < 100) {
    errors.push('Reconnect interval must be at least 100ms')
  }

  if (config.heartbeatInterval < 5000) {
    errors.push('Heartbeat interval must be at least 5 seconds')
  }

  return errors
}

/**
 * Gets the validated WebSocket configuration for the current or given host.
 */
export function getWebSocketConfig(hostname?: string | null): WebSocketConfig {
  const config =
    hostname !== undefined && hostname !== null
      ? buildWebSocketConfig(hostname)
      : defaultWebSocketConfig
  const errors = validateWebSocketConfig(config)

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('WebSocket configuration errors:', errors)
    throw new Error(`Invalid WebSocket configuration: ${errors.join(', ')}`)
  }

  return config
}

/**
 * WebSocket message types for AWS API Gateway
 */
export enum WebSocketMessageAction {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
  PING = 'ping',
  PONG = 'pong',
  NOTIFICATION = 'notification',
  ERROR = 'error',
  SUBSCRIBED = 'subscribed',
  UNSUBSCRIBED = 'unsubscribed',
}

/**
 * WebSocket connection states
 */
export enum WebSocketConnectionState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTING = 'DISCONNECTING',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
}

/**
 * Standard WebSocket message format for AWS API Gateway
 */
export interface WebSocketMessage {
  action: WebSocketMessageAction | string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  channel?: string
  userId?: string
  timestamp?: number
  requestId?: string
}
